// useEffectiveJobs(jobs) → { rows: Eff[], observe }      (DESIGN_SPEC §12.17)
// Every loaded job with what its run says beyond the record (peek.ts): effective state, verdict
// (stream › record › behavior report), phase, event count. The list never decides a row from a
// lagging record alone. Rows keep their identity while nothing about them changes (memoized rows).
//   observe(el, id) → cleanup   reports a row's visibility; live peeks run only for visible rows
// Behavior reports are read through React Query (["artifacts", id], ["artifact-json", id, sha],
// same-origin), so the report page reuses them.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { keys, pickArtifact } from "@/hooks";
import { api, isApiError } from "@/lib/api";
import type { VerdictSnap } from "@/lib/derive/types";
import { reportVerdict } from "@/lib/derive/verdict";
import type { Artifact, BehaviorReport, Job } from "@/types";
import { effectiveOf, type Eff } from "./listState";
import { peeker, type PeekInfo, type PeekStatus } from "./peek";

async function behaviorVerdict(qc: QueryClient, job: Job): Promise<VerdictSnap | null> {
  const artifacts = await qc.fetchQuery({
    queryKey: keys.artifacts(job.id),
    queryFn: async (): Promise<Artifact[]> => {
      try {
        return (await api.artifacts(job.id)).artifacts ?? [];
      } catch (err) {
        if (isApiError(err) && err.status === 409) return [];
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const art = pickArtifact(artifacts, "behavior-report");
  if (!art) return null;
  const report = await qc.fetchQuery({
    queryKey: keys.artifactJson(job.id, art.sha256),
    queryFn: () => api.artifactJson<BehaviorReport>(job.id, art.sha256),
    staleTime: Infinity,
    retry: false,
  });
  return reportVerdict({ verdict: null }, report);
}

type Memo = { job: Job; info?: PeekInfo; status: PeekStatus; eff: Eff };

export function useEffectiveJobs(jobs: readonly Job[] | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    peeker.setLoader((job) => behaviorVerdict(qc, job));
  }, [qc]);
  useEffect(() => {
    if (jobs) peeker.sync(jobs);
  }, [jobs]);
  useEffect(() => () => peeker.stop(), []);

  const version = useSyncExternalStore(peeker.subscribe, peeker.version, peeker.version);
  const memo = useRef(new Map<string, Memo>());

  const rows = useMemo(() => {
    const next = new Map<string, Memo>();
    const out = (jobs ?? []).map((job) => {
      const { status, info } = peeker.read(job.id);
      const prev = memo.current.get(job.id);
      const eff = prev && prev.job === job && prev.info === info && prev.status === status ? prev.eff : effectiveOf(job, { status, info });
      next.set(job.id, { job, info, status, eff });
      return eff;
    });
    memo.current = next;
    return out;
    // `version` (the peeker's change counter) is what re-derives the rows
  }, [jobs, version]);

  const io = useRef<IntersectionObserver | null>(null);
  useEffect(
    () => () => {
      io.current?.disconnect();
      io.current = null;
    },
    [],
  );

  const observe = useCallback((el: HTMLElement, id: string): (() => void) => {
    if (typeof IntersectionObserver !== "function") return () => undefined;
    if (!io.current) {
      io.current = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            const rowId = (e.target as HTMLElement).dataset.peek;
            if (rowId) peeker.visible(rowId, e.isIntersecting);
          }
        },
        { rootMargin: "160px 0px" },
      );
    }
    const obs = io.current;
    el.dataset.peek = id;
    obs.observe(el);
    return () => {
      obs.unobserve(el);
      peeker.visible(id, false);
    };
  }, []);

  return { rows, observe };
}
